import type { EntityWritePayload } from '../components/generic/crudTypes';
import api from './api';
import { mutate } from 'swr';

export const revalidate = (key: string | string[]) => {
  mutate(key);
};

const revalidateAppointments = () => {
  mutate(
    (key) => typeof key === 'string' && key.startsWith('/crm-clients-appointments'),
    undefined,
    { revalidate: true },
  );
};

// Client mutations
export const createClient = async (data: EntityWritePayload, files?: File[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));

  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-clients', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate('/crm-clients');
  return response.data;
};

export const updateClient = async (id: number, data: EntityWritePayload, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));

  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  if (filesToDetach && filesToDetach.length > 0) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }

  const response = await api.patch(`/crm-clients/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate(`/crm-clients/${id}`);
  mutate('/crm-clients');
  return response.data;
};

export const deleteClient = async (id: number) => {
  await api.delete(`/crm-clients/${id}`);
  mutate('/crm-clients');
};

// Todo mutations
export const createTodo = async (data: EntityWritePayload, files?: File[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));

  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-todos', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate('/crm-todos');
  return response.data;
};

export const updateTodo = async (id: number, data: EntityWritePayload, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  if (filesToDetach?.length) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.patch(`/crm-todos/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate(`/crm-todos/${id}`);
  mutate('/crm-todos');
  return response.data;
};

export const deleteTodo = async (id: number) => {
  await api.delete(`/crm-todos/${id}`);
  mutate('/crm-todos');
};

// Appointment mutations
export const createAppointment = async (data: EntityWritePayload, files?: File[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));

  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-clients-appointments', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  revalidateAppointments();
  return response.data;
};

export const updateAppointment = async (id: number, data: EntityWritePayload, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));

  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  if (filesToDetach && filesToDetach.length > 0) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }

  const response = await api.patch(`/crm-clients-appointments/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate(`/crm-clients-appointments/${id}`);
  revalidateAppointments();
  return response.data;
};

export const deleteAppointment = async (id: number) => {
  await api.delete(`/crm-clients-appointments/${id}`);
  revalidateAppointments();
};

// File storage helpers
export const permanentDeleteFileAttachment = async (id: number) => {
  await api.delete(`/file-attachment/${id}/permanent`);
  mutate('/file-attachment/deleted');
};

export const deleteOrphanedFileAttachments = async (): Promise<{ deleted: number }> => {
  const res = await api.delete<{ deleted: number }>('/file-attachment/audit/orphaned');
  mutate('/file-attachment/deleted');
  return res.data;
};

export const deleteFilesWithoutRecord = async (): Promise<{ deleted: number }> => {
  const res = await api.delete<{ deleted: number }>('/file-attachment/audit/files-without-record');
  return res.data;
};

// Parameter mutations
export const createTaxAuthority = async (data: EntityWritePayload) => {
  const response = await api.post('/p-tax-authority', data);
  mutate('/p-tax-authority?manage=1');
  return response.data;
};

export const updateTaxAuthority = async (id: number, data: EntityWritePayload) => {
  const response = await api.patch(`/p-tax-authority/${id}`, data);
  mutate(`/p-tax-authority/${id}`);
  mutate('/p-tax-authority?manage=1');
  return response.data;
};

export const deleteTaxAuthority = async (id: number) => {
  await api.delete(`/p-tax-authority/${id}`);
  mutate('/p-tax-authority?manage=1');
};

export const createCountry = async (data: EntityWritePayload) => {
  const response = await api.post('/p-country', data);
  mutate('/p-country?manage=1');
  return response.data;
};

export const updateCountry = async (id: number, data: EntityWritePayload) => {
  const response = await api.patch(`/p-country/${id}`, data);
  mutate(`/p-country/${id}`);
  mutate('/p-country?manage=1');
  return response.data;
};

export const deleteCountry = async (id: number) => {
  await api.delete(`/p-country/${id}`);
  mutate('/p-country?manage=1');
};

export const createAppStatus = async (data: EntityWritePayload) => {
  const response = await api.post('/p-crm-app-status', data);
  mutate('/p-crm-app-status?manage=1');
  return response.data;
};

export const updateAppStatus = async (id: number, data: EntityWritePayload) => {
  const response = await api.patch(`/p-crm-app-status/${id}`, data);
  mutate(`/p-crm-app-status/${id}`);
  mutate('/p-crm-app-status?manage=1');
  return response.data;
};

export const deleteAppStatus = async (id: number) => {
  await api.delete(`/p-crm-app-status/${id}`);
  mutate('/p-crm-app-status?manage=1');
};

export const createAppDuration = async (data: EntityWritePayload) => {
  const response = await api.post('/p-crm-duration', data);
  mutate('/p-crm-duration?manage=1');
  return response.data;
};

export const updateAppDuration = async (id: number, data: EntityWritePayload) => {
  const response = await api.patch(`/p-crm-duration/${id}`, data);
  mutate(`/p-crm-duration/${id}`);
  mutate('/p-crm-duration?manage=1');
  return response.data;
};

export const deleteAppDuration = async (id: number) => {
  await api.delete(`/p-crm-duration/${id}`);
  mutate('/p-crm-duration?manage=1');
};

export const createTag = async (data: EntityWritePayload) => {
  const response = await api.post('/tags', data);
  mutate('/tags?manage=1');
  return response.data;
};

export const updateTag = async (id: number, data: EntityWritePayload) => {
  const response = await api.patch(`/tags/${id}`, data);
  mutate(`/tags/${id}`);
  mutate('/tags?manage=1');
  return response.data;
};

export const deleteTag = async (id: number) => {
  await api.delete(`/tags/${id}`);
  mutate('/tags?manage=1');
};

// User mutations
export const createUser = async (data: EntityWritePayload) => {
  const response = await api.post('/users', data);
  mutate('/users');
  return response.data;
};

export const updateUser = async (id: number, data: EntityWritePayload) => {
  const updateData = { ...data };
  if (updateData.userPassword === '' || updateData.userPassword === undefined) {
    delete updateData.userPassword;
  }

  const response = await api.patch(`/users/${id}`, updateData);
  mutate(`/users/${id}`);
  mutate('/users');
  return response.data;
};

export const deleteUser = async (id: number) => {
  await api.delete(`/users/${id}`);
  mutate('/users');
};

export const rebuildUserEmailFolderStats = async (userId: number) => {
  await api.post(`/emails/folder-stats/rebuild/user/${userId}`);
};

export const sendUserInvitation = async (data: { email: string; role?: string }) => {
  const response = await api.post('/users/invitations', data);
  mutate('/users');
  return response.data;
};
